import React, { useState, useContext } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { NotificationContainer } from '../components/ui/Notification';
import { AuthContext } from '../App';
import { SettingsIcon, BuildingIcon, DollarSignIcon, BellIcon, ShieldIcon, FileTextIcon, UserIcon, SaveIcon, TrashIcon, AlertCircleIcon, CheckIcon, UploadIcon, DownloadIcon, ExternalLinkIcon, InfoIcon, MailIcon, KeyIcon, GlobeIcon } from 'lucide-react';
import { mockPolicyDocuments } from '../utils/mockData';
export function Settings() {
  const {
    userInfo
  } = useContext(AuthContext);
  // State for profile form
  const [profileForm, setProfileForm] = useState({
    name: userInfo.name,
    email: userInfo.email,
    phone: '555-123-4567',
    timezone: 'America/Los_Angeles',
    language: 'en-US'
  });
  // State for password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  // State for notification preferences
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailApprovals: true,
    emailRejections: true,
    emailReminders: true,
    browserNotifications: false
  });
  // State for expense preferences
  const [expensePreferences, setExpensePreferences] = useState({
    defaultCurrency: 'USD',
    receiptReminders: true,
    autoSaveDrafts: true
  });
  // Form errors
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  // State for notifications
  const [notifications, setNotifications] = useState([]);
  // Loading states
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);
  const [isNotificationSaving, setIsNotificationSaving] = useState(false);
  const [isExpenseSaving, setIsExpenseSaving] = useState(false);
  // Handle profile form change
  const handleProfileChange = e => {
    const {
      name,
      value
    } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (profileErrors[name]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  // Handle password form change
  const handlePasswordChange = e => {
    const {
      name,
      value
    } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  // Handle notification preference change
  const handleNotificationPreferenceChange = e => {
    const {
      name,
      checked
    } = e.target;
    setNotificationPreferences(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  // Handle expense preference change
  const handleExpensePreferenceChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setExpensePreferences(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  // Validate profile form
  const validateProfileForm = () => {
    const errors = {};
    if (!profileForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!profileForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!profileForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle profile save
  const handleProfileSave = e => {
    e.preventDefault();
    if (!validateProfileForm()) return;
    setIsProfileSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsProfileSaving(false);
      addNotification({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile information has been updated successfully.'
      });
    }, 1000);
  };
  // Handle password save
  const handlePasswordSave = e => {
    e.preventDefault();
    if (!validatePasswordForm()) return;
    setIsPasswordSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsPasswordSaving(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      addNotification({
        type: 'success',
        title: 'Password Updated',
        message: 'Your password has been changed successfully.'
      });
    }, 1000);
  };
  // Handle notification preferences save
  const handleNotificationSave = e => {
    e.preventDefault();
    setIsNotificationSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsNotificationSaving(false);
      addNotification({
        type: 'success',
        title: 'Notification Preferences Saved',
        message: 'Your notification preferences have been updated.'
      });
    }, 1000);
  };
  // Handle expense preferences save
  const handleExpenseSave = e => {
    e.preventDefault();
    setIsExpenseSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsExpenseSaving(false);
      addNotification({
        type: 'success',
        title: 'Expense Preferences Saved',
        message: 'Your expense preferences have been updated.'
      });
    }, 1000);
  };
  // Add notification
  const addNotification = notification => {
    const id = Date.now();
    setNotifications(prev => [...prev, {
      id,
      ...notification
    }]);
  };
  // Remove notification
  const removeNotification = id => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  return <div className="space-y-6" data-id="element-2052">
      {/* Notifications */}
      <NotificationContainer notifications={notifications} onClose={removeNotification} data-id="element-2053" />
      <div data-id="element-2054">
        <h1 className="text-2xl font-bold text-gray-100" data-id="element-2055">Account Settings</h1>
        <p className="text-gray-400 mt-1" data-id="element-2056">
          Manage your profile and preferences
        </p>
      </div>
      {/* Profile Settings */}
      <Card data-id="element-2057">
        <div className="p-4 border-b border-gray-700" data-id="element-2058">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-2059">
            Profile Information
          </h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-2060">
            Update your personal information
          </p>
        </div>
        <form onSubmit={handleProfileSave} data-id="element-2061">
          <div className="p-6 space-y-4" data-id="element-2062">
            <div className="flex flex-col md:flex-row gap-4" data-id="element-2063">
              <div className="w-full md:w-1/3" data-id="element-2064">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2065">
                  Full Name <span className="text-red-500" data-id="element-2066">*</span>
                </label>
                <div className="relative" data-id="element-2067">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2068">
                    <UserIcon size={16} className="text-gray-400" data-id="element-2069" />
                  </div>
                  <input type="text" name="name" value={profileForm.name} onChange={handleProfileChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${profileErrors.name ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2070" />
                </div>
                {profileErrors.name && <p className="mt-1 text-sm text-red-500" data-id="element-2071">
                    {profileErrors.name}
                  </p>}
              </div>
              <div className="w-full md:w-1/3" data-id="element-2072">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2073">
                  Email Address <span className="text-red-500" data-id="element-2074">*</span>
                </label>
                <div className="relative" data-id="element-2075">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2076">
                    <MailIcon size={16} className="text-gray-400" data-id="element-2077" />
                  </div>
                  <input type="email" name="email" value={profileForm.email} onChange={handleProfileChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${profileErrors.email ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2078" />
                </div>
                {profileErrors.email && <p className="mt-1 text-sm text-red-500" data-id="element-2079">
                    {profileErrors.email}
                  </p>}
              </div>
              <div className="w-full md:w-1/3" data-id="element-2080">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2081">
                  Phone Number <span className="text-red-500" data-id="element-2082">*</span>
                </label>
                <input type="text" name="phone" value={profileForm.phone} onChange={handleProfileChange} className={`w-full px-3 py-2 bg-gray-700 border ${profileErrors.phone ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2083" />
                {profileErrors.phone && <p className="mt-1 text-sm text-red-500" data-id="element-2084">
                    {profileErrors.phone}
                  </p>}
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4" data-id="element-2085">
              <div className="w-full md:w-1/2" data-id="element-2086">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2087">
                  Timezone
                </label>
                <div className="relative" data-id="element-2088">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2089">
                    <GlobeIcon size={16} className="text-gray-400" data-id="element-2090" />
                  </div>
                  <select name="timezone" value={profileForm.timezone} onChange={handleProfileChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-2091">
                    <option value="America/Los_Angeles" data-id="element-2092">
                      Pacific Time (US & Canada)
                    </option>
                    <option value="America/Denver" data-id="element-2093">
                      Mountain Time (US & Canada)
                    </option>
                    <option value="America/Chicago" data-id="element-2094">
                      Central Time (US & Canada)
                    </option>
                    <option value="America/New_York" data-id="element-2095">
                      Eastern Time (US & Canada)
                    </option>
                    <option value="UTC" data-id="element-2096">UTC</option>
                  </select>
                </div>
              </div>
              <div className="w-full md:w-1/2" data-id="element-2097">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2098">
                  Language
                </label>
                <select name="language" value={profileForm.language} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-2099">
                  <option value="en-US" data-id="element-2100">English (United States)</option>
                  <option value="en-GB" data-id="element-2101">English (United Kingdom)</option>
                  <option value="fr-FR" data-id="element-2102">French (France)</option>
                  <option value="es-ES" data-id="element-2103">Spanish (Spain)</option>
                  <option value="de-DE" data-id="element-2104">German (Germany)</option>
                </select>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end" data-id="element-2105">
            <Button type="submit" disabled={isProfileSaving} data-id="element-2106">
              {isProfileSaving ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2107"></div>
                  Saving...
                </> : <>
                  <SaveIcon size={16} className="mr-1.5" data-id="element-2108" />
                  Save Changes
                </>}
            </Button>
          </div>
        </form>
      </Card>
      {/* Password Settings */}
      <Card data-id="element-2109">
        <div className="p-4 border-b border-gray-700" data-id="element-2110">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-2111">Change Password</h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-2112">
            Update your password to maintain security
          </p>
        </div>
        <form onSubmit={handlePasswordSave} data-id="element-2113">
          <div className="p-6 space-y-4" data-id="element-2114">
            <div data-id="element-2115">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2116">
                Current Password <span className="text-red-500" data-id="element-2117">*</span>
              </label>
              <div className="relative" data-id="element-2118">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2119">
                  <KeyIcon size={16} className="text-gray-400" data-id="element-2120" />
                </div>
                <input type="password" name="currentPassword" value={passwordForm.currentPassword} onChange={handlePasswordChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2121" />
              </div>
              {passwordErrors.currentPassword && <p className="mt-1 text-sm text-red-500" data-id="element-2122">
                  {passwordErrors.currentPassword}
                </p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2123">
              <div data-id="element-2124">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2125">
                  New Password <span className="text-red-500" data-id="element-2126">*</span>
                </label>
                <div className="relative" data-id="element-2127">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2128">
                    <KeyIcon size={16} className="text-gray-400" data-id="element-2129" />
                  </div>
                  <input type="password" name="newPassword" value={passwordForm.newPassword} onChange={handlePasswordChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${passwordErrors.newPassword ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2130" />
                </div>
                {passwordErrors.newPassword && <p className="mt-1 text-sm text-red-500" data-id="element-2131">
                    {passwordErrors.newPassword}
                  </p>}
              </div>
              <div data-id="element-2132">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2133">
                  Confirm New Password <span className="text-red-500" data-id="element-2134">*</span>
                </label>
                <div className="relative" data-id="element-2135">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2136">
                    <KeyIcon size={16} className="text-gray-400" data-id="element-2137" />
                  </div>
                  <input type="password" name="confirmPassword" value={passwordForm.confirmPassword} onChange={handlePasswordChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-2138" />
                </div>
                {passwordErrors.confirmPassword && <p className="mt-1 text-sm text-red-500" data-id="element-2139">
                    {passwordErrors.confirmPassword}
                  </p>}
              </div>
            </div>
            <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4 flex items-start" data-id="element-2140">
              <ShieldIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-2141" />
              <div data-id="element-2142">
                <p className="text-sm text-blue-300" data-id="element-2143">Password requirements:</p>
                <ul className="mt-1 text-xs text-blue-200 list-disc list-inside" data-id="element-2144">
                  <li data-id="element-2145">Minimum 8 characters long</li>
                  <li data-id="element-2146">Include at least one uppercase letter</li>
                  <li data-id="element-2147">Include at least one number</li>
                  <li data-id="element-2148">Include at least one special character</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end" data-id="element-2149">
            <Button type="submit" disabled={isPasswordSaving} data-id="element-2150">
              {isPasswordSaving ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2151"></div>
                  Updating...
                </> : <>
                  <SaveIcon size={16} className="mr-1.5" data-id="element-2152" />
                  Update Password
                </>}
            </Button>
          </div>
        </form>
      </Card>
      {/* Notification Preferences */}
      <Card data-id="element-2153">
        <div className="p-4 border-b border-gray-700" data-id="element-2154">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-2155">
            Notification Preferences
          </h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-2156">
            Manage how and when you receive notifications
          </p>
        </div>
        <form onSubmit={handleNotificationSave} data-id="element-2157">
          <div className="p-6 space-y-4" data-id="element-2158">
            <h3 className="text-md font-medium text-gray-200" data-id="element-2159">
              Email Notifications
            </h3>
            <div className="space-y-3" data-id="element-2160">
              <div className="flex items-center" data-id="element-2161">
                <input id="emailApprovals" name="emailApprovals" type="checkbox" checked={notificationPreferences.emailApprovals} onChange={handleNotificationPreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2162" />
                <label htmlFor="emailApprovals" className="ml-2 text-sm text-gray-200" data-id="element-2163">
                  Expense approvals
                </label>
              </div>
              <div className="flex items-center" data-id="element-2164">
                <input id="emailRejections" name="emailRejections" type="checkbox" checked={notificationPreferences.emailRejections} onChange={handleNotificationPreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2165" />
                <label htmlFor="emailRejections" className="ml-2 text-sm text-gray-200" data-id="element-2166">
                  Expense rejections
                </label>
              </div>
              <div className="flex items-center" data-id="element-2167">
                <input id="emailReminders" name="emailReminders" type="checkbox" checked={notificationPreferences.emailReminders} onChange={handleNotificationPreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2168" />
                <label htmlFor="emailReminders" className="ml-2 text-sm text-gray-200" data-id="element-2169">
                  Weekly expense reminders
                </label>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700" data-id="element-2170">
              <h3 className="text-md font-medium text-gray-200 mb-3" data-id="element-2171">
                Browser Notifications
              </h3>
              <div className="flex items-center" data-id="element-2172">
                <input id="browserNotifications" name="browserNotifications" type="checkbox" checked={notificationPreferences.browserNotifications} onChange={handleNotificationPreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2173" />
                <label htmlFor="browserNotifications" className="ml-2 text-sm text-gray-200" data-id="element-2174">
                  Enable browser notifications
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-2175">
                Receive notifications in your browser when you're using the
                application
              </p>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end" data-id="element-2176">
            <Button type="submit" disabled={isNotificationSaving} data-id="element-2177">
              {isNotificationSaving ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2178"></div>
                  Saving...
                </> : <>
                  <SaveIcon size={16} className="mr-1.5" data-id="element-2179" />
                  Save Preferences
                </>}
            </Button>
          </div>
        </form>
      </Card>
      {/* Expense Preferences */}
      <Card data-id="element-2180">
        <div className="p-4 border-b border-gray-700" data-id="element-2181">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-2182">
            Expense Preferences
          </h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-2183">
            Customize your expense entry experience
          </p>
        </div>
        <form onSubmit={handleExpenseSave} data-id="element-2184">
          <div className="p-6 space-y-4" data-id="element-2185">
            <div data-id="element-2186">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-2187">
                Default Currency
              </label>
              <div className="relative" data-id="element-2188">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2189">
                  <DollarSignIcon size={16} className="text-gray-400" data-id="element-2190" />
                </div>
                <select name="defaultCurrency" value={expensePreferences.defaultCurrency} onChange={handleExpensePreferenceChange} className="pl-10 w-full md:w-1/3 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-2191">
                  <option value="USD" data-id="element-2192">USD - US Dollar</option>
                  <option value="EUR" data-id="element-2193">EUR - Euro</option>
                  <option value="GBP" data-id="element-2194">GBP - British Pound</option>
                  <option value="CAD" data-id="element-2195">CAD - Canadian Dollar</option>
                  <option value="AUD" data-id="element-2196">AUD - Australian Dollar</option>
                  <option value="JPY" data-id="element-2197">JPY - Japanese Yen</option>
                </select>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700" data-id="element-2198">
              <h3 className="text-md font-medium text-gray-200 mb-3" data-id="element-2199">
                Expense Entry Options
              </h3>
              <div className="space-y-3" data-id="element-2200">
                <div className="flex items-center" data-id="element-2201">
                  <input id="receiptReminders" name="receiptReminders" type="checkbox" checked={expensePreferences.receiptReminders} onChange={handleExpensePreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2202" />
                  <label htmlFor="receiptReminders" className="ml-2 text-sm text-gray-200" data-id="element-2203">
                    Show receipt reminders for expenses over policy limits
                  </label>
                </div>
                <div className="flex items-center" data-id="element-2204">
                  <input id="autoSaveDrafts" name="autoSaveDrafts" type="checkbox" checked={expensePreferences.autoSaveDrafts} onChange={handleExpensePreferenceChange} className="h-4 w-4 text-blue-600 border-gray-500 rounded focus:ring-blue-500 bg-gray-700" data-id="element-2205" />
                  <label htmlFor="autoSaveDrafts" className="ml-2 text-sm text-gray-200" data-id="element-2206">
                    Automatically save expense drafts
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end" data-id="element-2207">
            <Button type="submit" disabled={isExpenseSaving} data-id="element-2208">
              {isExpenseSaving ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2209"></div>
                  Saving...
                </> : <>
                  <SaveIcon size={16} className="mr-1.5" data-id="element-2210" />
                  Save Preferences
                </>}
            </Button>
          </div>
        </form>
      </Card>
    </div>;
}